/**
 * Proactive Agent Service
 * 
 * Calls the proactive API endpoint on page load to get contextual messages.
 * Displays messages and triggers TTS.
 */

import { speak } from '../../tts/index.js';
import { showNotification, showProactiveNotification } from '../index.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

export interface ProactiveMessage {
  message: string;
  type: 'urgent' | 'informative' | 'supportive' | 'planning' | 'reflective';
  context?: {
    schedules?: any[];
    todos?: any[];
    recentDecisions?: any[];
    facts?: any[];
  };
  timestamp: number;
}

export interface PendingNotification {
  id: string;
  message: string;
  messageType: ProactiveMessage['type'];
  deliveredAt: number;
  channel: string;
  reminderId?: string;
  priority?: number;
  metadata?: Record<string, any>;
}

/**
 * Fetch proactive message from API
 */
export async function fetchProactiveMessage(): Promise<ProactiveMessage | null> {
  try {
    // Get auth token from localStorage if available
    const authToken = localStorage.getItem('authToken');
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/api/proactive`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      // If not authenticated, that's okay - just return null
      if (response.status === 401) {
        return null;
      }
      throw new Error(`Proactive API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // API returns message at root level, not nested in data.message
    if (Array.isArray(data.pendingNotifications)) {
      data.pendingNotifications.forEach((notification: PendingNotification) => {
        showNotification({
          serverNotificationId: notification.id,
          message: notification.message,
          type: mapMessageTypeToNotificationType(notification.messageType),
          duration: 0,
        });
      });
    }

    if (data.success && data.message) {
      return {
        message: data.message,
        type: data.type || 'informative',
        context: data.context || {},
        timestamp: data.timestamp || Date.now(),
      } as ProactiveMessage;
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

export function mapMessageTypeToNotificationType(
  messageType: PendingNotification['messageType']
): 'info' | 'success' | 'warning' | 'error' {
  switch (messageType) {
    case 'urgent':
      return 'error';
    case 'supportive':
      return 'success';
    case 'planning':
      return 'warning';
    default:
      return 'info';
  }
}

/**
 * Display proactive message and trigger TTS
 */
export async function showProactiveMessage(message: ProactiveMessage): Promise<void> {
  // Show as notification
  showProactiveNotification(message.message);
  
  // Emit event for UI to display the message (for other components that might want it)
  const event = new CustomEvent('proactive-message', {
    detail: message,
  });
  window.dispatchEvent(event);
  
  // Trigger TTS
  try {
    await speak(message.message);
  } catch (error) {
  }
}

/**
 * Initialize proactive agent (call on page load)
 */
export async function initializeProactiveAgent(): Promise<void> {
  // Wait a bit for page to fully load
  setTimeout(async () => {
    // Fetch initial proactive message on page load
    const message = await fetchProactiveMessage();
    if (message) {
      await showProactiveMessage(message);
    }

    // Connect to WebSocket for real-time proactive messages
    try {
      const { connectProactiveWebSocket } = await import('./websocket.js');
      connectProactiveWebSocket();
    } catch (error) {
      // Continue without WebSocket - page load message still works
    }
  }, 1000); // 1 second delay to let page settle
}

